<script>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { invoiceReport as baseInvoiceReportConfig } from "./data";
import { useInvoiceStore } from "@/stores/invoiceStore";

export default {
  data() {
    return {
      title: "Invoice Report",
      text: "HR",
      text1: "Invoice Report",
      //invoiceReport: invoiceReport,
    };
  },
  methods: {
    toggleHeader() {
      document.getElementById("collapse-header").classList.toggle("active");
      document.body.classList.toggle("header-collapse");
    },
  },
  setup() {
    const store = useInvoiceStore();
    const dateRangeInput = ref(null);

    const meta = computed(() => store.meta);

    // chart state
    const chartOptions = ref({ ...baseInvoiceReportConfig.sline });
    const chartSeries = ref([
      { name: "Total Invoices", data: [] },
      { name: "Paid Invoices", data: [] },
    ]);

    // 🔹 YEAR DROPDOWN STATE
    const selectedYearLabel = ref("This Year");
    const yearOptions = ref([
      { label: "This Year", value: "this_year" },
      { label: String(moment().year()), value: moment().year() },
      { label: String(moment().year() - 1), value: moment().year() - 1 },
      { label: String(moment().year() - 2), value: moment().year() - 2 },
    ]);

    // Local stats for report (NOT the global stats used elsewhere)
    const reportStats = ref({
      total: 0,
      outstanding: 0,
      draft: 0,
      overdue: 0,
      total_pct: 0,
      outstanding_pct: 0,
      draft_pct: 0,
      overdue_pct: 0,
      total_invoices: 0,
      paid_invoices: 0,
      overdue_invoices: 0,
      partially_paid_invoices: 0,
      unpaid_invoices: 0,
      revenue: 0,
    });

    const loading = computed(() => store.loading);

    const totalInvoices = computed(() => reportStats.value.total_invoices ?? 0);
    const paidInvoices = computed(() => reportStats.value.paid_invoices ?? 0);
    const partiallyPaidInvoices = computed(
      () => reportStats.value.partially_paid_invoices ?? 0
    );
    const overdueInvoices = computed(
      () => reportStats.value.overdue_invoices ?? 0
    );
    const unpaidInvoices = computed(
      () => reportStats.value.unpaid_invoices ?? 0
    );
    const revenue = computed(() =>
      Number(reportStats.value.revenue || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

    async function reload(extra = {}) {
      const params = {
        page: extra.page ?? store.meta.current_page ?? 1,
        status: (extra.status ?? store.filters.status) || undefined,
        from: (extra.from ?? store.filters.from) || undefined,
        to: (extra.to ?? store.filters.to) || undefined,
        q: (extra.q ?? store.filters.q) || undefined,
        per_page: (extra.per_page ?? store.meta.per_page) || 25,
      };

      // list + stats + series
      const [_, stats, seriesData] = await Promise.all([
        store.fetchAll(params),
        store.statsForRange(params).then((s) => {
          reportStats.value = { ...reportStats.value, ...s };
        }),
        store.seriesForRange(params),
      ]);

      // Build chart
      const categories = seriesData.map((r) => r.label);
      const totalData = seriesData.map((r) => Number(r.total || 0));
      const paidData = seriesData.map((r) => Number(r.paid || 0));

      chartSeries.value = [
        { name: "Total Invoices", data: totalData },
        { name: "Paid Invoices", data: paidData },
      ];

      chartOptions.value = {
        ...baseInvoiceReportConfig.sline,
        xaxis: {
          ...(baseInvoiceReportConfig.sline?.xaxis || {}),
          categories,
        },
      };
    }
    function applyDateRange() {
      const val = dateRangeInput.value?.value || "";
      const parts = val.split("-").map((s) => s.trim());
      if (parts.length === 2) {
        const from = moment(parts[0], ["DD/MM/YYYY", "M/D/YYYY"]).format(
          "YYYY-MM-DD"
        );
        const to = moment(parts[1], ["DD/MM/YYYY", "M/D/YYYY"]).format(
          "YYYY-MM-DD"
        );

        store.filters.from = from !== "Invalid date" ? from : null;
        store.filters.to = to !== "Invalid date" ? to : null;

        reload();
      }
    }

    function applyDateRangeFromInput() {
      const val = dateRangeInput.value?.value || "";
      const parts = val.split("-").map((s) => s.trim());
      if (parts.length === 2) {
        const from = moment(parts[0], ["DD/MM/YYYY", "M/D/YYYY"]).format(
          "YYYY-MM-DD"
        );
        const to = moment(parts[1], ["DD/MM/YYYY", "M/D/YYYY"]).format(
          "YYYY-MM-DD"
        );

        if (from !== "Invalid date") store.filters.from = from;
        if (to !== "Invalid date") store.filters.to = to;

        reload({
          from: store.filters.from,
          to: store.filters.to,
          page: 1,
        });
      }
    }

    // 🔹 YEAR FILTER HANDLER
    function applyYearFilter(option) {
      selectedYearLabel.value = option.label;

      let fromMoment;
      let toMoment;

      if (option.value === "this_year") {
        const y = moment().year();
        fromMoment = moment().year(y).startOf("year");
        toMoment = moment().year(y).endOf("year");
      } else {
        const y = option.value;
        fromMoment = moment().year(y).startOf("year");
        toMoment = moment().year(y).endOf("year");
      }

      const from = fromMoment.format("YYYY-MM-DD");
      const to = toMoment.format("YYYY-MM-DD");

      // update store filters
      store.filters.from = from;
      store.filters.to = to;

      // update visible text in the date-range input for consistency
      if (dateRangeInput.value) {
        dateRangeInput.value.value = `${fromMoment.format(
          "DD/MM/YYYY"
        )} - ${toMoment.format("DD/MM/YYYY")}`;
      }

      // reload data + chart
      reload({
        from,
        to,
        page: 1,
      });
    }

    function setStatus(status) {
      // Map UI labels to backend statuses
      // We'll use real statuses: paid, pending, draft, overdue
      store.filters.status = status || "";
      reload();
    }

    // Simple sort-by-period using the same filters.from/to
    // function setSort(mode) {
    //   const today = moment();
    //   let from = null;
    //   let to = null;

    //   switch (mode) {
    //     case "last7":
    //       from = today.clone().subtract(6, "days").format("YYYY-MM-DD");
    //       to = today.format("YYYY-MM-DD");
    //       break;
    //     case "last30":
    //       from = today.clone().subtract(29, "days").format("YYYY-MM-DD");
    //       to = today.format("YYYY-MM-DD");
    //       break;
    //     case "thisMonth":
    //       from = today.clone().startOf("month").format("YYYY-MM-DD");
    //       to = today.clone().endOf("month").format("YYYY-MM-DD");
    //       break;
    //     case "lastMonth":
    //       from = today
    //         .clone()
    //         .subtract(1, "month")
    //         .startOf("month")
    //         .format("YYYY-MM-DD");
    //       to = today
    //         .clone()
    //         .subtract(1, "month")
    //         .endOf("month")
    //         .format("YYYY-MM-DD");
    //       break;
    //     default:
    //       from = null;
    //       to = null;
    //   }

    //   store.filters.from = from;
    //   store.filters.to = to;

    //   if (dateRangeInput.value && from && to) {
    //     dateRangeInput.value.value =
    //       moment(from).format("DD/MM/YYYY") +
    //       " - " +
    //       moment(to).format("DD/MM/YYYY");
    //   }

    //   reload();
    // }

    function setSort(mode) {
      const today = moment();
      let from = null;
      let to = null;

      switch (mode) {
        case "last7":
          from = today.clone().subtract(6, "days");
          to = today.clone();
          break;
        case "last30":
          from = today.clone().subtract(29, "days");
          to = today.clone();
          break;
        case "thisMonth":
          from = today.clone().startOf("month");
          to = today.clone().endOf("month");
          break;
        case "lastMonth":
          from = today.clone().subtract(1, "month").startOf("month");
          to = today.clone().subtract(1, "month").endOf("month");
          break;
        default:
          from = null;
          to = null;
      }

      if (from && to) {
        const fromStr = from.format("YYYY-MM-DD");
        const toStr = to.format("YYYY-MM-DD");

        store.filters.from = fromStr;
        store.filters.to = toStr;

        if (dateRangeInput.value) {
          dateRangeInput.value.value =
            from.format("DD/MM/YYYY") + " - " + to.format("DD/MM/YYYY");
        }

        reload({
          from: fromStr,
          to: toStr,
          page: 1,
        });
      } else {
        // reset
        store.filters.from = null;
        store.filters.to = null;
        if (dateRangeInput.value) {
          dateRangeInput.value.value = "";
        }
        reload({ page: 1 });
      }
    }

    async function exportPdf() {
      try {
        const blob = await store.exportPdf();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-report-${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        // error already handled by store toast
        console.error("Export invoice report failed", e);
      }
    }

    async function exportExcel() {
      try {
        const blob = await store.exportExcel();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoice-report-${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Export invoice report Excel failed", e);
      }
    }

    // onMounted(async () => {
    //   // Initial default: last 7 days
    //   const start = moment().subtract(6, "days");
    //   const end = moment();

    //   store.filters.from = start.format("YYYY-MM-DD");
    //   store.filters.to = end.format("YYYY-MM-DD");
    //   if (dateRangeInput.value) {
    //     const start = moment().subtract(6, "days");
    //     const end = moment();

    //     dateRangeInput.value.value = `${start.format(
    //       "DD/MM/YYYY"
    //     )} - ${end.format("DD/MM/YYYY")}`;

    //     new DateRangePicker(
    //       dateRangeInput.value,
    //       {
    //         startDate: start,
    //         endDate: end,
    //         ranges: {
    //           Today: [moment(), moment()],
    //           Yesterday: [
    //             moment().subtract(1, "days"),
    //             moment().subtract(1, "days"),
    //           ],
    //           "Last 7 Days": [moment().subtract(6, "days"), moment()],
    //           "Last 30 Days": [moment().subtract(29, "days"), moment()],
    //           "This Month": [
    //             moment().startOf("month"),
    //             moment().endOf("month"),
    //           ],
    //           "Last Month": [
    //             moment().subtract(1, "month").startOf("month"),
    //             moment().subtract(1, "month").endOf("month"),
    //           ],
    //         },
    //       },
    //       () => applyDateRangeFromInput()
    //     );

    //     // also sync filters for initial load
    //     store.filters.from = start.format("YYYY-MM-DD");
    //     store.filters.to = end.format("YYYY-MM-DD");
    //   }

    //   await reload();
    // });

    onMounted(async () => {
      const start = moment().subtract(6, "days");
      const end = moment();

      // initial filters
      store.filters.from = start.format("YYYY-MM-DD");
      store.filters.to = end.format("YYYY-MM-DD");

      if (dateRangeInput.value) {
        const inputEl = dateRangeInput.value;

        // initial text in the input
        inputEl.value = `${start.format("DD/MM/YYYY")} - ${end.format(
          "DD/MM/YYYY"
        )}`;

        new DateRangePicker(
          inputEl,
          {
            startDate: start,
            endDate: end,
            autoUpdateInput: false,
            locale: {
              format: "DD/MM/YYYY", // we control the visible format
            },
            ranges: {
              Today: [moment(), moment()],
              Yesterday: [
                moment().subtract(1, "days"),
                moment().subtract(1, "days"),
              ],
              "Last 7 Days": [moment().subtract(6, "days"), moment()],
              "Last 30 Days": [moment().subtract(29, "days"), moment()],
              "This Month": [
                moment().startOf("month"),
                moment().endOf("month"),
              ],
              "Last Month": [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
              ],
            },
          },
          (start, end) => {
            // ✅ Use the real moment objects here
            const from = start.format("YYYY-MM-DD");
            const to = end.format("YYYY-MM-DD");

            store.filters.from = from;
            store.filters.to = to;

            // update visible input text
            inputEl.value = `${start.format("DD/MM/YYYY")} - ${end.format(
              "DD/MM/YYYY"
            )}`;

            reload({
              from,
              to,
              page: 1,
            });
          }
        );
      }

      await reload();
    });

    return {
      dateRangeInput,
      store,
      reportStats,
      loading,
      meta,
      totalInvoices,
      paidInvoices,
      partiallyPaidInvoices,
      overdueInvoices,
      unpaidInvoices,
      revenue,
      setStatus,
      setSort,
      exportPdf,
      chartOptions,
      chartSeries,
      exportExcel,
      applyDateRangeFromInput,
      selectedYearLabel,
      yearOptions,
      applyYearFilter,
    };
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportExcel"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid">
        <!-- Summary Cards -->
        <div class="col-xl-7 d-flex">
          <div class="row flex-fill">
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Total Invoice</span
                      >
                      <h5>{{ totalInvoices }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3">
                      <!-- you can later replace with pct -->
                      &nbsp;
                    </span>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge-warning"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Partially Paid</span
                      >
                      <h5>{{ partiallyPaidInvoices }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3"
                      >&nbsp;</span
                    >
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge-success"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Paid Invoices</span
                      >
                      <h5>{{ paidInvoices }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3"
                      >&nbsp;</span
                    >
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge-purple"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Overdue Invoices</span
                      >
                      <h5>{{ overdueInvoices }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3"
                      >&nbsp;</span
                    >
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge-danger"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Unpaid Invoices</span
                      >
                      <h5>{{ unpaidInvoices }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3"
                      >&nbsp;</span
                    >
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card invoice-report flex-fill dq-stat-card">
                <span class="invoice-report-badge-skyblue"></span>
                <div
                  class="card-body d-flex flex-wrap align-items-center justify-content-between"
                >
                  <div
                    class="d-flex align-items-center flex-column overflow-hidden"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Revenue</span
                      >
                      <h5>KES {{ revenue }}</h5>
                    </div>
                  </div>
                  <div
                    class="d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <span class="badge badge-sm badge-success me-3"
                      >&nbsp;</span
                    >
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md br-10 bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary">
                        <i class="ti ti-file-invoice"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Chart (now dynamic based on backend series) -->
        <!-- Chart (now wired to yearOptions + applyYearFilter) -->
        <div class="col-xl-5 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-area-line text-danger"></i
                  ></span>
                  <h5>Invoice</h5>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYearLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li v-for="opt in yearOptions" :key="opt.value">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="applyYearFilter(opt)"
                      >
                        {{ opt.label }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="card-body py-0">
              <div id="invoice-report">
                <apexchart
                  type="area"
                  height="250"
                  :options="chartOptions"
                  :series="chartSeries"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List + filters -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Invoice List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- (Optional) amount range – currently UI only, you can wire min/max later -->

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Select Status
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('')"
                    >All</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('paid')"
                    >Paid</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('pending')"
                    >Pending</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('draft')"
                    >Draft</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setStatus('overdue')"
                    >Overdue</a
                  >
                </li>
              </ul>
            </div>

            <!-- Sort by period -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : Last 7 Days
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('last7')"
                    >Last 7 Days</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('last30')"
                    >Last 30 Days</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('thisMonth')"
                    >This Month</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="setSort('lastMonth')"
                    >Last Month</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <invoice-report-table :loading="loading" :meta="meta" />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


